import { Activity, TSession } from '@valuecase/common'

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@radix-ui/react-collapsible'
import { Duration } from 'luxon'
import { useCallback, useMemo, useState } from 'react'
import { cn } from '../../lib/utils'
import { Badge, Tooltip, TooltipContent, TooltipTrigger } from '../../components/ui'
import { IonIcon } from '../../Icons/IonIcon'
import { useDateFormatting } from '../../utils/DateUtils'
import { ActivityItem } from './ActivityItem'

const SessionCollapsible = ({
  session,
  activities,
  latestTimeReadActivities,
  openOnRender = false,
  score = 0,
  onTrackEvent,
  onShowPageOrBlock,
  onShowActivityTaskDetails,
  getBlockTitle,
}: {
  session: TSession
  activities: Activity[]
  latestTimeReadActivities: Date | null
  score?: number
  openOnRender?: boolean
  onTrackEvent?: (event: { event: string; eventProperties: Record<string, any> }) => void
  onShowPageOrBlock: (e: React.MouseEvent<HTMLElement>, activity: Activity) => void
  onShowActivityTaskDetails: (activity: Activity) => void
  getBlockTitle: (data: any) => string | undefined
}) => {
  const { formatTime } = useDateFormatting()

  const newActivitiesInSessionCount = useMemo(() => {
    if (!latestTimeReadActivities) {
      return 0
    }

    return activities.filter((activity) => new Date(activity.createdAt) > latestTimeReadActivities)
      .length
  }, [activities, latestTimeReadActivities])

  const [open, setOpen] = useState(openOnRender || newActivitiesInSessionCount > 0)

  const { duration } = session

  return (
    <Collapsible
      open={open}
      onOpenChange={(open) => {
        setOpen(open)

        if (open) {
          onTrackEvent?.({
            event: 'activity-feed-expand',
            eventProperties: {
              sessionActivitiesCount: session.activityCount,
              sessionDuration: session.duration,
            },
          })
        }
      }}
    >
      <CollapsibleTrigger
        className={cn(
          'flex flex-col gap-2 w-full bg-white hover:bg-[#fafbfc] transition-colors',
          'rounded-t p-2 border-primary-s3 outline-none',
          `sticky top-0 z-10 border-l-8 border-solid`,
          {
            rounded: !open,
          },
        )}
        // primary s4 color, with Tailwind it's a bit complicated, so we stick to plain CSS
        style={{ borderColor: `rgba(119, 147, 255, ${score}%)` }}
      >
        <div className={'flex items-center justify-between w-full'}>
          <h3 className={'text-xs font-semibold text-grey-s6'}>
            {session.name || session.email || 'Anonymous Stakeholder'}
          </h3>
          <div className={'flex items-center gap-3'}>
            {newActivitiesInSessionCount > 0 && (
              <Badge variant={'subtle-info'} size={'small'}>
                {newActivitiesInSessionCount} New
              </Badge>
            )}
            {open && <IonIcon name={'chevron-up'} className={'w-3 h-3 text-grey-s5'} />}
            {!open && <IonIcon name={'chevron-down'} className={'w-3 h-3 text-grey-s5'} />}
          </div>
        </div>
        <div className={'flex items-center justify-between w-full'}>
          <span className={'text-xs text-grey-s4'}>{formatTime(session.startTime)}</span>
          <div className={'flex items-center gap-3'}>
            <Tooltip>
              <TooltipTrigger className={'flex items-center gap-1'}>
                <IonIcon name={'c_activity'} className={'w-3 h-3 text-grey-s4'} />
                <span className={'text-xs text-grey-s4'}>
                  {activities.length}
                  {activities.length < session.activityCount && <> / {session.activityCount}</>}
                </span>
              </TooltipTrigger>
              <TooltipContent>Activities</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger className={'flex items-center gap-1'}>
                <IonIcon name={'hourglass-outline'} className={'w-3 h-3 text-grey-s4'} />
                <div className={'flex gap-1 text-xs text-grey-s4'}>
                  {(duration.hours ?? 0) !== 0 && <span>{duration.hours}h</span>}
                  {(duration.minutes ?? 0) !== 0 && <span>{duration.minutes}m</span>}
                  {(duration.seconds ?? 0) !== 0 && <span>{duration.seconds}s</span>}
                </div>
              </TooltipTrigger>
              <TooltipContent>Time spent</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className={'w-full bg-white/65 rounded-b mt-[1px]'}>
        <div className={'flex flex-col gap-4 p-4'}>
          {activities.map((activity) => {
            return (
              <ActivityItem
                key={activity.id}
                activity={activity}
                latestTimeReadActivities={latestTimeReadActivities}
                onShowPageOrBlock={onShowPageOrBlock}
                onShowActivityTaskDetails={onShowActivityTaskDetails}
                getBlockTitle={getBlockTitle}
              />
            )
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

export function ActivitiesPerDay({
  date,
  activities,
  latestTimeReadActivities,
  sessions,
  onShowPageOrBlock,
  onShowActivityTaskDetails,
  getBlockTitle,
}: {
  date: string
  activities: Activity[]
  latestTimeReadActivities: Date | null
  sessions: TSession[]
  onShowPageOrBlock: (e: React.MouseEvent<HTMLElement>, activity: Activity) => void
  onShowActivityTaskDetails: (activity: Activity) => void
  getBlockTitle: (data: any) => string | undefined
}) {
  const { formatDateWithRelativeWords } = useDateFormatting()

  const dayHeadingText = useMemo(() => {
    return formatDateWithRelativeWords(date, 'long')
  }, [date, formatDateWithRelativeWords])

  const groupedActivitiesBySession = useMemo(() => {
    const activitiesPerSession: { session?: TSession; activities: Activity[] }[] = []

    let lastSession: { session?: TSession; activities: Activity[] } | undefined

    for (const activity of activities) {
      const session = sessions.find((session) => session.id === activity.sessionId)

      if (!lastSession || lastSession.session?.id !== activity.sessionId) {
        const existingSession = activitiesPerSession.find(
          (session) => session.session?.id === activity.sessionId,
        )
        if (existingSession) {
          // This ensures that session continuity is maintained and not split into multiple groups
          // due to ungrouped activities (such as user notifications) occurring in between grouped sessions.
          existingSession.activities.push(activity)
          continue
        }

        lastSession = { session, activities: [activity] }
        activitiesPerSession.push(lastSession)
      } else {
        lastSession.activities.push(activity)
      }
    }

    return activitiesPerSession
  }, [activities, sessions])

  const maxSessionsDuration = useMemo(() => {
    const durations = sessions.map((session) => Duration.fromObject(session.duration))
    return durations.reduce(
      (max, current) => (current > max ? current : max),
      Duration.fromMillis(0),
    )
  }, [sessions])

  const activityScore = useCallback(
    (sessionDuration: Duration) => {
      const maxDurationInMinutes = maxSessionsDuration.as('minutes')
      const durationInMinutesOfSession = sessionDuration.as('minutes')
      const percentageOfMax = (durationInMinutesOfSession / maxDurationInMinutes) * 100
      // Ensure that scores start at 20% and move in 20% increments from there
      return Math.max(20, Math.ceil(percentageOfMax / 20) * 20)
    },
    [maxSessionsDuration],
  )

  return (
    <div className={'flex flex-col gap-4'}>
      <div className={'flex items-center justify-center w-full'}>
        <div className={'h-[1px] bg-grey-s2 grow'} />
        <h3 className={'text-xs text-grey-s5 font-semibold uppercase px-2'}>{dayHeadingText}</h3>
        <div className={'h-[1px] bg-grey-s2 grow'} />
      </div>

      {groupedActivitiesBySession.map((group, index) => {
        if (!group.session) {
          return (
            <div className={'flex flex-col gap-4'} key={group.activities[0].id}>
              {group.activities.map((activity) => {
                return (
                  <ActivityItem
                    showName={activity.type !== 'DIRECT_LINK_TO_FILE_CLICKED'}
                    key={activity.id}
                    activity={activity}
                    latestTimeReadActivities={latestTimeReadActivities}
                    onShowPageOrBlock={onShowPageOrBlock}
                    onShowActivityTaskDetails={onShowActivityTaskDetails}
                    getBlockTitle={getBlockTitle}
                  />
                )
              })}
            </div>
          )
        }

        return (
          <SessionCollapsible
            openOnRender={
              group.session.id ===
              sessions.sort(
                (a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime(),
              )[0].id
            }
            key={`${group.session.id}-${group.activities[0].id}`}
            session={group.session}
            score={activityScore(Duration.fromObject(group.session.duration))}
            activities={group.activities}
            latestTimeReadActivities={latestTimeReadActivities}
            onShowPageOrBlock={onShowPageOrBlock}
            onShowActivityTaskDetails={onShowActivityTaskDetails}
            getBlockTitle={getBlockTitle}
          />
        )
      })}
    </div>
  )
}
